<script>
  import { onMount, onDestroy } from "svelte";
  import L from "leaflet";
  import { VISITOR_GET_URL, VISITOR_RECORD_URL } from "./config.js";

  const SESSION_KEY = "ggcross_visit_recorded";
  const regionNames =
    typeof Intl !== "undefined" && Intl.DisplayNames
      ? new Intl.DisplayNames(["en"], { type: "region" })
      : null;

  let mapEl;
  let map;
  let markers = [];
  let totalVisits = 0;
  let totalCountries = 0;
  let loadError = null;
  let configured = Boolean(VISITOR_GET_URL);

  function markerRadius(count) {
    return Math.min(18, Math.max(6, 4 + Math.sqrt(count) * 2));
  }

  function countryDisplayName(code) {
    if (!code || code === "ZZ") return "Unknown";
    try {
      return regionNames?.of(code) || code;
    } catch (_) {
      return code;
    }
  }

  // Keep each marker on the world copy nearest the current map center
  function wrapLngToCenter(lng, centerLng) {
    let wrapped = lng;
    while (wrapped - centerLng > 180) wrapped -= 360;
    while (wrapped - centerLng < -180) wrapped += 360;
    return wrapped;
  }

  function syncMarkerWraps() {
    if (!map) return;
    const centerLng = map.getCenter().lng;
    for (const marker of markers) {
      marker.setLatLng([
        marker._baseLat,
        wrapLngToCenter(marker._baseLng, centerLng),
      ]);
    }
  }

  async function loadMapData() {
    if (!VISITOR_GET_URL) {
      loadError = "Visitor map API URL is not configured yet.";
      return;
    }

    const response = await fetch(VISITOR_GET_URL, { method: "GET", mode: "cors" });
    if (!response.ok) {
      throw new Error(`Failed to load visitor data (${response.status})`);
    }
    const data = await response.json();
    totalVisits = data.total_visits || 0;
    totalCountries = data.total_countries || 0;

    if (!map) return;

    for (const marker of markers) {
      map.removeLayer(marker);
    }
    markers = [];

    const centerLng = map.getCenter().lng;
    for (const country of data.countries || []) {
      const name = countryDisplayName(country.country_code);
      const marker = L.circleMarker(
        [country.lat, wrapLngToCenter(country.lon, centerLng)],
        {
          radius: markerRadius(country.visit_count),
          color: "#60a5fa",
          weight: 1.5,
          fillColor: "#93c5fd",
          fillOpacity: 0.85,
        }
      )
        .bindPopup(
          `<strong>${name}</strong><br/>Visits: ${country.visit_count}`
        )
        .addTo(map);
      marker._baseLat = country.lat;
      marker._baseLng = country.lon;
      markers.push(marker);
    }
  }

  async function recordVisit() {
    if (!VISITOR_RECORD_URL) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
      const response = await fetch(VISITOR_RECORD_URL, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (response.ok || response.status === 204) {
        sessionStorage.setItem(SESSION_KEY, "1");
      }
    } catch (err) {
      console.warn("Failed to record visit", err);
    }
  }

  onMount(async () => {
    map = L.map(mapEl, {
      worldCopyJump: true,
      minZoom: 1,
      scrollWheelZoom: false,
    }).setView([20, 0], 1.5);

    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ",
        maxZoom: 16,
      }
    ).addTo(map);

    map.on("move", syncMarkerWraps);

    try {
      await loadMapData();
    } catch (err) {
      console.error(err);
      loadError = err.message || "Failed to load visitor map.";
    }

    // Record after map fetch so the first paint can include prior visits;
    // this session's visit appears on the next page load.
    await recordVisit();

    setTimeout(() => map && map.invalidateSize(), 50);
  });

  onDestroy(() => {
    if (map) {
      map.off("move", syncMarkerWraps);
      map.remove();
      map = null;
    }
    markers = [];
  });
</script>

<svelte:head>
  <link
    rel="stylesheet"
    href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
    integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY="
    crossorigin=""
  />
</svelte:head>

<section class="visitor-map-section">
  <h3>Global Visitor Map</h3>

  <div class="map-container">
    <div class="map" bind:this={mapEl}></div>
    {#if loadError}
      <div class="map-overlay">{loadError}</div>
    {:else if !configured}
      <div class="map-overlay">
        Configure visitor Function URLs in <code>src/config.js</code> after
        terraform apply.
      </div>
    {/if}
  </div>

  <div class="map-stats">
    <div class="map-stat">
      Total visits: <strong>{totalVisits}</strong>
    </div>
    <div class="map-stat">
      Countries: <strong>{totalCountries}</strong>
    </div>
  </div>
</section>

<style>
  .visitor-map-section {
    margin: 2rem 0 0 0;
  }

  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    color: #475569;
    margin: 2rem 0 1rem 0;
    border-left: 3px solid #64748b;
    padding-left: 1rem;
  }

  .map-container {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
  }

  .map {
    width: 100%;
    height: 300px;
  }

  .map :global(.leaflet-container) {
    width: 100%;
    height: 100%;
    background: #f8fafc;
    font: inherit;
  }

  .map-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    text-align: center;
    color: #64748b;
    background: rgba(248, 250, 252, 0.85);
    pointer-events: none;
  }

  .map-overlay code {
    color: #475569;
  }

  .map-stats {
    display: flex;
    gap: 2rem;
    margin-top: 1rem;
    flex-wrap: wrap;
  }

  .map-stat {
    color: #64748b;
    font-size: 0.95rem;
  }

  .map-stat strong {
    color: #1e293b;
    font-size: 1.15rem;
  }

  @media (max-width: 768px) {
    .map {
      height: 240px;
    }
  }
</style>
